import React from "react";
import styles from "./closed-filter-card.module.scss";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { handleSelectCard } from "../../../../utils/cardNavigation";
import { resetObjectStyle } from "../../../../utils/helpers-for-2d-map";
import { focusOnFeature } from "../../../../services/actions/map";
import { useTranslation } from "react-i18next";
import {
  renderStatusTag,
  renderTypeOfUseTag,
} from "../../../../utils/render-tags";
import Button from "../../../ui-kit/button/button";
import { generateLinkToEms } from "../../../../utils/genereate-link-to-ems";
import positionIcon from "../../../../images/position.svg";
import Tooltip from "../../../ui-kit/tooltip/tooltip";

interface UniversalCardProps {
  data: any;
  type: "InvestmentOpportunities" | "Plots" | "SpacesForRent";
  showInvestBtn?: boolean;
  showBtns?: boolean;
  className?: string;
}

const ClosedFilterCard: React.FC<UniversalCardProps> = ({
  data,
  type,
  showInvestBtn = true,
  showBtns = true,
  className,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const fidString = searchParams.get("fid");
  const isActive = fidString === data.fid;
  const hasFid = data.fid;

  const { t } = useTranslation();

  const containerClass = [
    styles.universalCard,
    !showInvestBtn && styles.noInvest,
    isActive && styles.activeCard,
  ]
    .filter(Boolean)
    .join(" ");

  const handleShowDetails = () => {
    handleSelectCard(dispatch, type, data.fid.toString(), false);
    resetObjectStyle();
  };

  const getTitle = () => {
    if (type === "Plots") return `${t("city-card.plotNumber")}: ${data.number}`;
    if (type === "InvestmentOpportunities")
      return data.invOpNameEn || t("city-card.noData");
    if (type === "SpacesForRent")
      return `${t("city-card.officeNumber")}: ${data.number}`;
  };

  return (
    <div
      id={`closed-filter-card-${data.fid}`}
      className={`${containerClass} ${className}`}
    >
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={isActive ? styles.activeTitle : ""}>
            {getTitle()}
          </span>
          <p>{data.area} m²</p>
        </div>
        <button
          className={styles.positionBtn}
          disabled={!data.geometry}
          onClick={() =>
            dispatch(focusOnFeature({ ...data, label: getTitle() }))
          }
        >
          <Tooltip disabled={!data.geometry} text={t("city-card.showOnMap")}>
            <img src={positionIcon} alt="Position" />
          </Tooltip>
        </button>
      </div>
      {type === "InvestmentOpportunities" && (
        <p className={styles.text}>
          {data.invOpNameAr || t("city-card.noData")}
        </p>
      )}
      {type === "SpacesForRent" && (
        <p className={styles.text}>
          {t("city-card.buildingLabel")} {data.building},{" "}
          {t("city-card.floorLabel", { floor: data.floorName })},{" "}
          {t("city-card.areaLabel", { area: data.area })}
        </p>
      )}
      {type === "Plots" && (
        <p className={styles.text}>
          {t("city-card.cityLabel")} {data.city}
        </p>
      )}
      <div className={styles.tags}>
        {type !== "InvestmentOpportunities" && renderStatusTag(data.status)}
        {renderTypeOfUseTag(data.typeOfUse)}
      </div>
      {type === "InvestmentOpportunities" && (
        <p className={styles.text}>
          {t("city-card.plotNumberLabel")} {data.number}
        </p>
      )}
      {type === "SpacesForRent" && (
        <p className={styles.text}>
          {t("city-card.cityLabel")} {data.city}
        </p>
      )}
      {showBtns && (
        <div
          style={{ paddingTop: type !== "Plots" ? "8px" : "0px" }}
          className={styles.groupBtn}
        >
          <button
            disabled={isActive || !hasFid}
            onClick={handleShowDetails}
            className={styles.detailsBtn}
          >
            {t("city-card.showDetails")}
            <span className={styles.arrowMask} />
          </button>
          {showInvestBtn && (
            <Button
              disabled={
                data.typeOfUse === "Governmental" || data.status === "Rented"
              }
              type="withIcon"
              className={styles.investBtn}
              onClick={() =>
                window.open(
                  generateLinkToEms(data.estateEn, data.fid),
                  "_blank",
                )
              }
            >
              {t("city-card.investHere")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ClosedFilterCard;
